import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Drawer,
  Divider,
  Chip,
} from "@mui/material";
import { toast } from "react-toastify";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
} from "../../../../app.interface";
import {
  adminOrdersService,
  Order,
  PosSession,
  OrderItem,
} from "../../services/admin-orders.service";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";
import { formatCurrency } from "../../../../utils/formatters";
import { formatDate } from "../../../../utils/date-utils";
import { useAuth } from "../../../auth/hooks/useAuth";
import { isEmpty } from "lodash";

// Extend Order to include IESDoc properties
export type OrderWithIESDoc = IESDoc<Order>;

interface OrderTableRow extends WithId, Record<string, unknown> {
  _id: string;
  order_id: string;
  session_id: string;
  store_id: string;
  branch_id: string;
  cashier_id: string;
  cashier_name: string;
  status: string;
  items: OrderItem[];
  payment: {
    method: string;
    status: string;
    transaction_id: string;
  };
  activity_logs: {
    action: string;
    changed_by: string;
    description: string;
    timestamp: number;
  }[];
  total_cost: number;
  raw_price: number;
  tax_amount: number;
  total_price: number;
  total_profit_margin: number;
  total_revenue: number;
  order_date: string;
  created_at: number;
  updated_at: number;
  is_deleted: boolean;
}

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
}> = ({ icon, label, value, valueColor }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        fontFamily: "Montserrat",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        color: valueColor || "text.primary",
        fontFamily: "Montserrat",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export const OrdersManager: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = React.useState<OrderWithIESDoc[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [selectedOrder, setSelectedOrder] =
    React.useState<OrderWithIESDoc | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState(false);

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Order Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter orders by their current status",
      options: [
        {
          id: "pending",
          label: "Pending",
          value: "pending",
          icon: "schedule",
          color: "#F59E0B",
          description: "Orders awaiting processing",
        },
        {
          id: "processing",
          label: "Processing",
          value: "processing",
          icon: "sync",
          color: "#3B82F6",
          description: "Orders being processed",
        },
        {
          id: "completed",
          label: "Completed",
          value: "completed",
          icon: "check_circle",
          color: "#059669",
          description: "Successfully completed orders",
        },
        {
          id: "cancelled",
          label: "Cancelled",
          value: "cancelled",
          icon: "cancel",
          color: "#DC2626",
          description: "Cancelled orders",
        },
      ],
    },
    {
      id: "payment",
      label: "Payment Status",
      type: "select",
      icon: "payments",
      description: "Filter orders by payment status",
      options: [
        {
          id: "paid",
          label: "Paid",
          value: "paid",
          icon: "check_circle",
          color: "#059669",
        },
        {
          id: "pending",
          label: "Pending",
          value: "pending",
          icon: "schedule",
          color: "#F59E0B",
        },
        {
          id: "failed",
          label: "Failed",
          value: "failed",
          icon: "error",
          color: "#DC2626",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort orders by field",
      options: [
        {
          id: "date_desc",
          label: "Date (Newest)",
          value: "date_desc",
          icon: "arrow_downward",
        },
        {
          id: "date_asc",
          label: "Date (Oldest)",
          value: "date_asc",
          icon: "arrow_upward",
        },
        {
          id: "total_desc",
          label: "Amount (Highest)",
          value: "total_desc",
          icon: "arrow_downward",
        },
        {
          id: "total_asc",
          label: "Amount (Lowest)",
          value: "total_asc",
          icon: "arrow_upward",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      field: "_id",
      header: "Order ID",
      width: "120px",
      id: "_id",
      type: "action",
      renderAction: (row: any) => (
        <Tooltip title={row._id}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {`O-${row._id?.substring(0, 8)?.toUpperCase()}`}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: "cashier",
      header: "Cashier",
      field: "cashier_name",
      type: "action",
      width: "200px",
      renderAction: (row: WithId & Record<string, any>) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Icon sx={{ fontSize: 20, color: "text.secondary" }}>person</Icon>
          <Typography variant="body2">{row.cashier_name}</Typography>
        </Box>
      ),
    },
    {
      id: "items",
      header: "Items",
      field: "items",
      type: "action",
      width: "300px",
      renderAction: (row: WithId & Record<string, any>) => {
        return (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {row.items.length} item
              {row.items.length !== 1 ? "s" : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.items.map((item: OrderItem) => item.name).join(", ")}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "total",
      header: "Total Amount",
      field: "total_price",
      type: "action",
      width: "150px",
      sortable: true,
      renderAction: (row: WithId & Record<string, any>) => (
        <Typography variant="body2" fontWeight={500}>
          {formatCurrency(row.total_price, "BHD")}
        </Typography>
      ),
    },
    {
      id: "payment",
      header: "Payment",
      field: "payment",
      type: "action",
      width: "150px",
      renderAction: (row: WithId & Record<string, any>) => {
        return (
          <Box display="flex" gap={1} alignItems="center">
            <Chip
              label={row.payment.method}
              size="small"
              icon={
                <Icon>
                  {row.payment.method === "CASH" ? "payments" : "credit_card"}
                </Icon>
              }
              sx={{ bgcolor: alpha("#0891B2", 0.1), color: "#0891B2" }}
            />
          </Box>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      type: "action",
      width: "120px",
      renderAction: (row: WithId & Record<string, any>) => (
        <Chip
          label={row.status}
          size="small"
          color={getStatusColor(row.status)}
        />
      ),
    },
    {
      id: "order_date",
      header: "Order Date",
      field: "order_date",
      type: "action",
      width: "180px",
      sortable: true,
      renderAction: (row: WithId & Record<string, any>) => (
        <Typography variant="body2">{formatDate(row.order_date)}</Typography>
      ),
    },
    // {
    //   id: "actions",
    //   header: "",
    //   field: "actions",
    //   type: "action",
    //   width: "100px",
    //   renderAction: (row: WithId & Record<string, any>) => (
    //     <div className="flex gap-2 justify-end">
    //       <Tooltip title="View Details" arrow placement="top">
    //         <IconButton
    //           size="small"
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             handleViewDetails(row as IESDoc);
    //           }}
    //           sx={{
    //             width: 34,
    //             height: 34,
    //             bgcolor: (theme) => alpha("#0891B2", 0.08),
    //             color: "#0891B2",
    //             backdropFilter: "blur(8px)",
    //             transition: "all 0.2s ease-in-out",
    //             "&:hover": {
    //               transform: "translateY(-1px)",
    //               bgcolor: (theme) => alpha("#0891B2", 0.12),
    //               boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
    //             },
    //             "&:active": {
    //               transform: "translateY(0)",
    //             },
    //           }}
    //         >
    //           <Icon sx={{ fontSize: 18 }}>visibility</Icon>
    //         </IconButton>
    //       </Tooltip>
    //     </div>
    //   ),
    // },
  ];

  const loadOrders = () => {
    if (user?.store_id && !isEmpty(user?.store_id)) {
      setLoading(true);
      adminOrdersService
        .listBusinessOrders({
          pageIndex: page,
          pageSize,
          sortField,
          sortOrder,
          status: filters.status || undefined,
          search_text: search || undefined,
          from_date: filters.from_date || undefined,
          to_date: filters.to_date || undefined,
        })
        .then((response) => {
          const data = response?.data || [];
          setOrders(data?.items || []);
          setTotalItems(data?.total?.value || 0);
        })
        .catch(() => {
          toast.error("Failed to load orders");
          setOrders([]);
          setTotalItems(0);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  React.useEffect(loadOrders, [
    page,
    pageSize,
    sortField,
    sortOrder,
    search,
    filters,
    user?.store_id,
  ]);

  const handleViewDetails = (order: IESDoc) => {
    setSelectedOrder(order as OrderWithIESDoc);
    setDetailsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setSelectedOrder(null);
    setDetailsDrawerOpen(false);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      const success = await adminOrdersService.updateOrderStatus(
        orderId,
        newStatus
      );
      if (success) {
        toast.success(`Order status updated to ${newStatus}`);
        loadOrders();
      } else {
        toast.error("Failed to update order status");
      }
    } catch (error) {
      toast.error("An error occurred while updating the order");
      console.error("Error updating order status:", error);
    }
  };

  const getStatusColor = (
    status: string
  ): "warning" | "info" | "success" | "error" | "default" => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "warning";
      case "processing":
        return "info";
      case "completed":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const renderOrderDetails = () => {
    if (!selectedOrder) return null;

    return (
      <Fragment>
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "#0891B2",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Icon>receipt_long</Icon>
            <Typography variant="subtitle1" fontWeight={600}>
              Order Details
            </Typography>
          </Box>
          <IconButton
            onClick={handleDrawerClose}
            size="small"
            sx={{
              color: "white",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          <Box display="grid" gap={3}>
            <DetailItem
              icon="tag"
              label="Order ID"
              value={`#${selectedOrder._id?.substring(0, 8)?.toUpperCase()}`}
            />
            <DetailItem
              icon="person"
              label="Cashier"
              value={selectedOrder.cashier_name}
            />
            <DetailItem
              icon="schedule"
              label="Order Date"
              value={formatDate(selectedOrder.order_date)}
            />
            <DetailItem
              icon="fiber_manual_record"
              label="Status"
              value={selectedOrder.status}
              valueColor={
                selectedOrder.status === "completed"
                  ? "#059669"
                  : selectedOrder.status === "cancelled"
                  ? "#DC2626"
                  : selectedOrder.status === "processing"
                  ? "#3B82F6"
                  : "#F59E0B"
              }
            />
          </Box>

          <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />

          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              color: "text.primary",
              fontFamily: "Montserrat",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon sx={{ fontSize: 20 }}>shopping_cart</Icon>
            Order Items
          </Typography>

          <Box display="grid" gap={2}>
            {selectedOrder.items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha("#0891B2", 0.04),
                  border: "1px solid",
                  borderColor: alpha("#0891B2", 0.1),
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(item.price, "BHD")} × {item.quantity}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(item.price * item.quantity, "BHD")}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.04),
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.1),
            }}
          >
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Subtotal
              </Typography>
              <Typography variant="body2">
                {formatCurrency(selectedOrder.raw_price, "BHD")}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Tax
              </Typography>
              <Typography variant="body2">
                {formatCurrency(selectedOrder.tax_amount, "BHD")}
              </Typography>
            </Box>
            <Divider sx={{ my: 1, borderColor: alpha("#0891B2", 0.1) }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight={500}>
                Total
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatCurrency(selectedOrder.total_amount)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />

          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              color: "text.primary",
              fontFamily: "Montserrat",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon sx={{ fontSize: 20 }}>payments</Icon>
            Payment Information
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.04),
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.1),
            }}
          >
            <DetailItem
              icon={
                selectedOrder.payment.method === "CASH"
                  ? "payments"
                  : "credit_card"
              }
              label="Payment Method"
              value={selectedOrder.payment.method}
            />
            <Box sx={{ mt: 2 }}>
              <DetailItem
                icon="receipt"
                label="Transaction Amount"
                value={formatCurrency(selectedOrder.total_price)}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <DetailItem
                icon="fiber_manual_record"
                label="Payment Status"
                value={selectedOrder.payment.status}
                valueColor={
                  selectedOrder.payment.status === "completed"
                    ? "#059669"
                    : selectedOrder.payment.status === "failed"
                    ? "#DC2626"
                    : "#F59E0B"
                }
              />
            </Box>
          </Box>

          {selectedOrder.status !== "completed" &&
            selectedOrder.status !== "cancelled" && (
              <>
                <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />
                <Box display="flex" gap={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Icon>check</Icon>}
                    onClick={() => {
                      handleStatusChange(
                        selectedOrder.order_number,
                        "completed"
                      );
                      handleDrawerClose();
                    }}
                    sx={{
                      bgcolor: "#059669",
                      "&:hover": {
                        bgcolor: "#047857",
                      },
                    }}
                  >
                    Complete Order
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<Icon>cancel</Icon>}
                    onClick={() => {
                      handleStatusChange(
                        selectedOrder.order_number,
                        "cancelled"
                      );
                      handleDrawerClose();
                    }}
                    sx={{
                      borderColor: "#DC2626",
                      color: "#DC2626",
                      "&:hover": {
                        borderColor: "#B91C1C",
                        bgcolor: alpha("#DC2626", 0.04),
                      },
                    }}
                  >
                    Cancel Order
                  </Button>
                </Box>
              </>
            )}
        </Box>
      </Fragment>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Orders Management"
        subtitle="View and manage customer orders"
        icon="receipt_long"
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search orders by ID or cashier name..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={orders}
          allowHeaderWrap
          columns={MasterColumns}
          variant="default"
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          loading={loading}
          header={{
            title: "Orders List",
            subtitle: "Your current list of orders",
          }}
          selectable
          onSelectionChange={(selected) => {
            console.log("Selected:", selected);
          }}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: () => loadOrders(),
            },
            export: {
              label: "Export Orders",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "orders-list",
              },
            },
          }}
          pagination={{
            total: totalItems,
            page: page,
            pageSize: pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          onRowClick={handleViewDetails}
          rowProps={{
            sx: {
              cursor: "pointer",
              "&:hover": {
                bgcolor: alpha("#0891B2", 0.04),
              },
            },
          }}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={detailsDrawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "100%",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          },
        }}
      >
        {renderOrderDetails()}
      </Drawer>
    </Box>
  );
};
